<template>
  <vx-tooltip :enabled="tooltipEnabled"
              :title="publishToggleTooltipTitle"
              :text="publishToggleTooltipText"
              position="bottom"
              class="flex items-center publish-toggle"
  >
    <base-switch color="success"
                 variant="base-switch-success"
                 :key="refreshKey"
                 :is-disabled="!isPublishToggleEnabled"
                 :id="publishAnswerToggleId"
                 :is-loading="isBusy"
                 :spinner-color="spinnerColor"
                 v-model="localPublished"
    />
  </vx-tooltip>
</template>

<script>
import toaster from '@/utils/toaster'
import answerModule from '@/store/modules/answer.module'
import previewModule from '@/store/modules/preview.module'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import WithLoader from '@/components/common/WithLoader.vue'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import tailwind from '../../../tailwind.config'
import { MIN_EXAMPLES } from '@/store/constants'
import editorUtils from '@/utils/editor.utils'
import botModule from '@/store/modules/bot.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'

export default {
  components: {
    WithLoader
  },

  props: {
    botId: {
      type: String,
      required: true
    },
    answer: {
      type: Object
    },
    tooltipEnabled: {
      type: Boolean,
      default: false
    },
    isAnswerLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      published: false,
      refreshKey: 0,
      isLoading: false
    }
  },

  mounted() {
    this.published = this.answerPublished
    this.resetToggle()
  },

  computed: {
    selectedAnswer: answerModule.getAnswer,
    answerList: answerModule.getAnswerList,
    isSaved: previewModule.isSaved,
    answerExamplesCount: answerModule.getAnswerExamplesCount,
    stateTree: chatBubbleModule.stateTree,

    publishAnswerToggleId() {
      return BUSY_BUTTONS.PUBLISH_ANSWER + this.answer.id
    },

    answerPublished() {
      return this.answer?.published
    },

    isBusy() {
      return busyButtonsModule.isButtonBusy(this.publishAnswerToggleId)
    },

    isEmptyStateTree() {
      return previewModule.isEmptyStateTree(this.stateTree)
    },

    isGptBot() {
      return botModule.isGptBot()
    },

    isEmailBot() {
      return botModule.isEmailBot()
    },

    localPublished: {
      get() {
        return this.published
      },
      set(newPublished) {
        if (!this.isSaved && this.published === false) {
          toaster.error('The <b>answer cannot be published</b>. You have to save the answer first.')
          this.resetToggle()
          return
        }
        this.updateAnswerByPublished(newPublished)
      }
    },

    hasLessThanMinExamples() {
      if (this.isGptBot) return false
      if (this.answer.intent.examplesCount === undefined) return true

      return this.answer.intent.examplesCount < MIN_EXAMPLES
    },

    isPublishToggleEnabled() {
      if (this.isAnswerLoading || !this.selectedAnswer) return false
      if (this.hasLessThanMinExamples) return false
      if (this.isEmailBot) return true
      if (this.selectedAnswer?.id === this.answer.id && this.isEmptyStateTree) return false

      return true
    },

    publishToggleTooltipTitle() {
      if (this.isPublishToggleEnabled) {
        return this.published ? 'Answer is published' : 'Answer is not published'
      }

      return this.hasLessThanMinExamples ?
        `<span class="flex text-left">At least ${MIN_EXAMPLES} examples ❌</span>` :
        'Empty answer ❌'
    },

    publishToggleTooltipText() {
      if (this.isPublishToggleEnabled) {
        return this.published ?
          'The Virtual Assistant will reply to your customers with this answer.' :
          'The Virtual Assistant will not use this answer.'
      }

      const tooltipText = ['To enable publish, you need:']
      if (this.hasLessThanMinExamples) {
        tooltipText.push(`- at least ${MIN_EXAMPLES} <strong>examples</strong>`)
      }
      if (this.isEmptyStateTree) {
        tooltipText.push('- a response in the answer')
      }

      return tooltipText.length > 1 ? tooltipText.join('<br/>') : ''
    },

    spinnerColor() {
      return this.published ? tailwind.theme.colors['gray-dark'] : tailwind.theme.colors.success
    },

    isAnswerSelected() {
      if (!this.answer || !this.selectedAnswer) return false

      return this.answer.id === this.selectedAnswer?.id
    }
  },

  methods: {
    async updateAnswerByPublished(published) {
      try {
        await editorUtils.updateAnswerByPublished(published, this.answer.id)
        this.published = published
      } catch (err) {
        this.resetToggle()
      }
    },

    resetToggle() {
      this.refreshKey = Math.random()
    }
  },

  watch: {
    'selectedAnswer.published'(value) {
      if (value === this.published) return

      this.published = value
      this.resetToggle()
    }
  }
}
</script>
